import { LoadingButton as MLoadingButton } from "@mui/lab";
import { Grid } from "@mui/material";
import MCard from "@mui/material/Card";
import MCardActions from "@mui/material/CardActions";
import MCardContent from "@mui/material/CardContent";
import MDivider from "@mui/material/Divider";
import MList from "@mui/material/List";
import MListItem from "@mui/material/ListItem";
import MListItemIcon from "@mui/material/ListItemIcon";
import MListItemText, { listItemTextClasses } from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { colors } from "@zapalacorp/tokens";

import { CustomerPortalListItem } from "../../../../CustomerPortalListItem";
import {
  CustomerPortalModal,
  CustomerPortalModalCard,
  CustomerPortalModalTitle,
} from "../../../../CustomerPortalModal";

type ButtonProps = {
  isClickable?: boolean;
};

const Button = styled(MLoadingButton, {
  shouldForwardProp(propName) {
    return !["isClickable"].includes(propName as string);
  },
})<ButtonProps>(({ isClickable = true }) => ({
  padding: "10px 12px",
  fontWeight: 600,
  fontSize: "14px",
  letterSpacing: "0.1px",
  textTransform: "none",
  cursor: isClickable ? "pointer" : "default",
  pointerEvents: isClickable ? "auto" : "none",
}));

interface CardProps {
  block?: boolean;
  fullHeight?: boolean;
}

const Card = styled(MCard, {
  shouldForwardProp(propName) {
    return !["fullHeight", "block"].includes(propName as string);
  },
})<CardProps>(({ block, fullHeight, theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: block ? "100%" : undefined,
  height: fullHeight ? "100%" : undefined,
  position: "relative",
  borderRadius: "12px",
  backgroundColor: theme.palette.secondary.main,
}));

const CardContent = styled(MCardContent)(() => ({
  display: "flex",
  flexDirection: "column",
  padding: "16px",
  gap: "16px",
}));

const CardActions = styled(MCardActions)(() => ({}));

const CardTitle = styled(Typography)(() => ({
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: "24px",
  letterSpacing: "0.15px",
}));

const Divider = styled(MDivider)(() => ({
  marginTop: "auto",
}));

const List = styled(MList)({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const ListItem = styled(MListItem)({});

const ListItemText = styled(MListItemText)({
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "0.25px",
  margin: 0,
});

const ListItemIcon = styled(MListItemIcon)({
  minWidth: "20px",
  minHeight: "20px",
  marginRight: "8px",
  marginTop: "0px",
});

const Modal = styled(CustomerPortalModal)({});

type ModalCardProps = {
  minWidth?: string;
};

const ModalCard = styled(CustomerPortalModalCard, {
  shouldForwardProp(propName) {
    return !["minWidth"].includes(propName as string);
  },
})<ModalCardProps>(({ theme, minWidth }) => ({
  width: "100%",
  marginTop: "24px",
  marginBottom: "24px",
  maxHeight: "min(calc(100dvh - 24px), 800px)",
  minWidth: minWidth || "auto",

  [theme.breakpoints.up("md")]: {
    maxWidth: "628px",
  },
}));

const ModalTitle = styled(CustomerPortalModalTitle)({});

const ModalListItem = styled(CustomerPortalListItem)(() => ({
  [`& .${listItemTextClasses.root}`]: {
    marginTop: "12px !important",
    marginBottom: "12px !important",
  },
}));

const CustomerInfosContainer = styled("div")(() => ({
  alignContent: "center",
  display: "flex",
  gap: "8px",
}));

const ContactBox = styled(Grid)(() => ({
  "& span:nth-child(1)": {
    color: colors.gray600,
    fontFamily: "Manrope",
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "20px",
    letterSpacing: "0.1px",
  },
  "& span:nth-child(2)": {
    color: colors.gray600,
    fontFamily: "Manrope",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    letterSpacing: "0.5px",
  },
}));

export {
  Button,
  Card,
  CardTitle,
  CardContent,
  CardActions,
  CustomerInfosContainer,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  ModalCard,
  ModalTitle,
  ModalListItem,
  ContactBox,
};
